import { useCallback, useEffect, useRef, useState } from 'react';

// Polyfill for ResizeObserver in environments where it is not defined (e.g., Jest)
if (
  typeof window !== 'undefined' &&
  typeof (window as any).ResizeObserver === 'undefined'
) {
  (window as any).ResizeObserver = class {
    // Simple polyfill: no-op constructor
    constructor() {}

    observe() {}
    unobserve() {}
    disconnect() {}
  };
}

/**
 * * Hook that uses ResizeObserver to detect if an element's content is clamped.
 * Returns a ref to attach to an element and a boolean indicating whether
 * the element's content is currently clipped (scrollHeight > clientHeight).
 *
 * Usage:
 *   const [ref, isClamped] = useIsClamped<HTMLSpanElement>();
 *   <Tooltip disableHoverListener={!isClamped}>
 *     <span ref={ref}>...</span>
 *   </Tooltip>
 */
function useIsClamped<T extends HTMLElement>(
  watchValue?: unknown
): [(node: T | null) => void, boolean] {
  const [isClamped, setIsClamped] = useState(false);
  const nodeRef = useRef<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef<number | null>(null);

  const check = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;

    const next = node.scrollHeight > node.clientHeight;
    setIsClamped((prev) => (prev !== next ? next : prev));
  }, []);

  const scheduleCheck = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      check();
    });
  }, [check]);

  const ref = useCallback(
    (node: T | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      nodeRef.current = node;
      if (!node) {
        return;
      }

      // Check immediately and also on resize (e.g. window resize, panel resize)
      scheduleCheck();
      const observer = new ResizeObserver(scheduleCheck);
      observer.observe(node);
      observerRef.current = observer;
    },
    [scheduleCheck]
  );

  useEffect(() => {
    scheduleCheck();
  }, [scheduleCheck, watchValue]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return [ref, isClamped];
}

export default useIsClamped;
